import { cn } from "@drpcrd/utils/shared";
import { Nullable } from "@drpcrd/utils";
import { formatCdnImg } from "@drpcrd/utils/formatters";
import { Building, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";

type Props = {
  /** The person shown large — the creator in the brand feed, the sending
   * agent in the creator app. Already-absolute URLs (e.g. a Clerk profile
   * photo) are used as-is; relative paths are routed through the CDN. Falls
   * back to a generic person icon when unavailable. */
  avatarUrl?: Nullable<string>;
  /** The sender's talent-agency logo. Already-absolute URLs (e.g. a Clerk
   * org image) are used as-is; relative paths are routed through the CDN.
   * Empty means no logo (the badge falls back to a building icon). */
  logoUrl?: Nullable<string>;
  className?: string;
};

/**
 * Avatar for a talent-agent chat sender — a person's photo with the agency's
 * logo overlaid as a small badge overflowing the bottom-right corner (so a
 * message reads as both "who" and "which agency"). Mirrors `CreatorAvatar`'s
 * country-flag badge pattern. Both images are decorative: the sender label
 * beside the bubble names the person and the agency, and the photo may be
 * the creator while the label names the agent. Brand senders use
 * `CompanyAvatar` instead — the workspace logo alone, as in the brand app's
 * sidebar.
 */
export const CompanySenderAvatar = ({
  avatarUrl,
  logoUrl,
  className,
}: Props) => {
  // `Campaigns.logo` defaults to "" rather than null — treat that as no
  // logo, otherwise `formatCdnImg` hands the badge the bare CDN host and
  // the image errors out.
  const toSrc = (url: Nullable<string>) =>
    !url ? undefined : url.startsWith("http") ? url : formatCdnImg(url);
  const avatarSrc = toSrc(avatarUrl);
  const badgeSrc = toSrc(logoUrl);

  return (
    <div className={cn("relative h-4 w-4 shrink-0", className)}>
      <Avatar className="h-4 w-4">
        <AvatarImage src={avatarSrc} alt="" />
        <AvatarFallback className="bg-primary-50">
          <User className="text-primary-500 h-[60%] w-[60%]" />
        </AvatarFallback>
      </Avatar>
      <Avatar className="border-background absolute -right-1 -bottom-1 h-2.5 w-2.5 border">
        <AvatarImage src={badgeSrc} alt="" />
        <AvatarFallback className="bg-neutral-200">
          <Building className="h-[60%] w-[60%] text-neutral-500" />
        </AvatarFallback>
      </Avatar>
    </div>
  );
};
